import React, { useState, useEffect } from "react";
import { Eye, EyeOff, BookOpen, Target, Users, CheckCircle2, Sparkles, ChevronLeft } from "lucide-react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config";
import { useNavigate } from "react-router-dom";
import { State } from "country-state-city";
import { useTranslation } from "react-i18next";
import SideButtons from "../../Components/SideButtons";
import { AnimatedBackground } from "../../Components/animated-background"; // ← garde si tu l'as

const initialState = {
  last_name_parent: "",
  first_name_parent: "",
  parent_phone: "",
  adresse: "",
  password: "",
  general_conditions_accepted: false,
};

export default function Inscription() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(initialState);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1, 2 ou 3
  const [passwordError, setPasswordError] = useState("");
  const [governorates, setGovernorates] = useState([]);
  const [conditions, setConditions] = useState([]);

  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pagesRes = await axios.get(`${config.SITE_API_URL}/page`,
          { headers: { 'x-api-key': config.SITE_API_KEY } }
        );
        const filtered = Array.isArray(pagesRes?.data)
          ? pagesRes.data.filter(
              (p) => p.title?.fr !== "À propos" && p.title?.ar !== "حول"
            )
          : [];
        setConditions(filtered);

      } catch (err) {
        toast.error(t("errors.fetch_classes_error"));
      }
    };

    fetchData();

    const tunisiaGov = State.getStatesOfCountry("TN");
    setGovernorates(tunisiaGov || []);
  }, []);

  const validatePassword = (pwd) => {
    if (!pwd) return t("errors.password_required");
    if (pwd.length < 10) return t("errors.password_min_length");
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/.test(pwd)) {
      return t("errors.password_requirements");
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
    } else {
      if (["customer_phone", "parent_phone"].includes(name) && value && !/^\d*$/.test(value)) {
        toast.error(t("errors.phone_numeric_error"));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));

      if (name === "password") {
        setPasswordError(validatePassword(value));
      }
    }
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      // Validation étape 1 : Parent
      const required = ["last_name_parent", "first_name_parent", "parent_phone", "adresse"];
      for (const field of required) {
        if (!formData[field]) {
          toast.error(`Le champ ${t(field)} est requis`);
          return;
        }
      }
      setCurrentStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const pwdError = validatePassword(formData.password);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }

    if (formData.password !== confirmPassword) {
      toast.error(t("errors.password_mismatch"));
      return;
    }

    if (!formData.general_conditions_accepted) {
      toast.error(t("errors.terms_not_accepted"));
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${config.SITE_API_URL}/register_customer`, formData, {
        headers: { 'x-api-key': config.SITE_API_KEY }
      });
      toast.success(t("register_success"));
      setTimeout(() => navigate("/login"), 2200);
    } catch (error) {
      toast.error(error?.response?.data?.msg || t("errors.register_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div
      dir={direction}
      className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white flex items-center justify-center p-4 min-md:p-8 overflow-hidden relative"
    >
          <AnimatedBackground />
      <ToastContainer position={direction === "rtl" ? "bottom-right" : "bottom-left"} theme="colored" />

      {/* Orbes décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-blue-300 to-blue-200 rounded-full opacity-30 animate-orb-float-1 blur-2xl" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-orange-300 to-orange-200 rounded-full opacity-25 animate-orb-float-2 blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-gradient-to-br from-cyan-300 to-blue-300 rounded-full opacity-35 animate-orb-float-3 blur-2xl" />
      </div>

      <div className="relative z-10">
        <div className="flex flex-col min-md:flex-row gap-8 min-md:gap-12 items-center">
          {/* Colonne gauche – inchangée */}
          <div className="hidden min-md:block animate-slideInRight">
            <div className="space-y-8">
            
                          {/* Slogan */}
                          <div className="space-y-3">
                            <h2 className="text-4xl min-md:text-5xl font-bold text-blue-900 leading-tight">
                              {t("welcome_back_learning")}
                            </h2>
                            <p className="text-lg text-blue-700">
                              {t("connect_to_continue_professional_training")}
                            </p>
                          </div>
            
                          {/* Features */}
                          <div className="space-y-6 pt-4">
                            <div className="flex gap-4 items-start">
                              <div className="w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-400/50">
                                <Target className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-blue-900 text-lg">{t("personalized_paths")}</h3>
                                <p className="text-sm text-blue-700">{t("courses_adapted_to_your_profile")}</p>
                              </div>
                            </div>
            
                            <div className="flex gap-4 items-start">
                              <div className="w-11 h-11 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-400/50">
                                <Users className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-blue-900 text-lg">{t("qualified_trainers")}</h3>
                                <p className="text-sm text-blue-700">{t("learn_from_professionals")}</p>
                              </div>
                            </div>
            
                            <div className="flex gap-4 items-start">
                              <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/50">
                                <BookOpen className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-blue-900 text-lg">{t("practical_content")}</h3>
                                <p className="text-sm text-blue-700">{t("exercises_projects_certificates")}</p>
                              </div>
                            </div>
                          </div>
                        </div>
          </div>

          {/* Colonne droite – Formulaire */}
          <div className="animate-fadeInUp w-full max-w-9xl">
            <div className="bg-white rounded-2xl shadow-2xl p-8 min-md:p-10 border border-blue-100 backdrop-blur-sm bg-opacity-95">
              <div className="">
                <div className="space-y-2 text-center min-md:text-left">
                  <h2 className="text-xxl font-bold text-blue-950 flex items-center justify-center gap-2">
                    {t("create_account")}
                    <Sparkles className="w-6 h-6 text-orange-400 animate-pulse" />
                  </h2>
                  <p className="text-blue-600">
                    Étape {currentStep} sur 2
                  </p>
                </div>

                {/* Barre de progression – maintenant 3 étapes */}
                <div className="flex items-center gap-2 relative">
                  {[1, 2].map((step) => (
                    <React.Fragment key={step}>
                      <div className="flex-1 flex flex-col items-center">
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                            currentStep >= step
                              ? step === currentStep
                                ? "bg-orange-500 text-white shadow-lg shadow-orange-400/60 scale-110"
                                : "bg-cyan-400 text-white"
                              : "bg-blue-100 text-blue-900"
                          }`}
                        >
                          {step}
                        </div>
                        <p className="text-xs text-blue-700 mt-1.5 whitespace-nowrap">
                          {step === 1 ? "Utilisateur" : "Sécurité"}
                        </p>
                      </div>
                      {step < 2 && (
                        <div className="absolute top-4 left-[calc(33.33%-8px)] right-[calc(33.33%-8px)] h-1 bg-blue-200 -z-10">
                          <div
                            className={`h-full bg-gradient-to-r from-orange-400 to-cyan-400 transition-all duration-500 ${
                              currentStep > step ? "w-full" : currentStep === step ? "w-1/2" : "w-0"
                            }`}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Succès */}
                {currentStep === 4 && (
                  <div className="bg-gradient-to-r from-green-50 to-cyan-50 border border-green-200 rounded-lg p-8 text-center space-y-4 animate-fadeInUp">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto animate-bounce" />
                    <h3 className="text-xl font-bold text-green-900">Inscription réussie !</h3>
                    <p className="text-green-800">Vérifiez votre email pour activer votre compte.</p>
                    <button
                      onClick={() => navigate("/login")}
                      className="mt-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white px-8 py-3 rounded-xl hover:shadow-xl transition-all"
                    >
                      Aller à la connexion
                    </button>
                  </div>
                )}

                {/* Étape 1 : Client */}
                {currentStep === 1 && (
                  <form onSubmit={handleNext} className="space-y-6">
                    <h3 className="text-lg font-semibold text-blue-900 mt-3">{t("parent_info")}</h3>
                    <div className="grid grid-cols-1 min-md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1.5">
                          {t("first_name_parent")} *
                        </label>
                        <input
                          name="first_name_parent"
                          value={formData.first_name_parent}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all"
                          placeholder={t("placeholders.first_name_parent")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1.5">
                          {t("last_name_parent")} *
                        </label>
                        <input
                          name="last_name_parent"
                          value={formData.last_name_parent}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all"
                          placeholder={t("placeholders.last_name_parent")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1.5">
                          {t("parent_phone")} *
                        </label>
                        <input
                          name="parent_phone"
                          value={formData.parent_phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all"
                          placeholder={t("placeholders.parent_phone")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1.5">
                          {t("governorate")} *
                        </label>
                        <select
                          name="adresse"
                          value={formData.adresse}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all"
                          required
                        >
                          <option value="">{t("select_governorate")}</option>
                          {governorates.map((g) => (
                            <option key={g.isoCode} value={g.name}>
                              {g.name.replace(" Governorate", "")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-semibold py-3.5 rounded-xl transition-all shadow-lg"
                      >
                        Continuer
                      </button>
                    </div>
                  </form>
                )}


                {/* Étape 2 : Mot de passe + Conditions */}
                {currentStep === 2 && (
                  <form onSubmit={handleSubmit} className="space-y-6 mt-3">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-blue-900">
                        {t("password")} *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                            passwordError ? "border-red-400" : "border-blue-200"
                          } bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all pr-12`}
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute ${direction === "rtl" ? "left-4" : "right-4"} top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-900`}
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {passwordError && <p className="text-red-600 text-sm mt-1">{passwordError}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-blue-900">
                        {t("confirm_password")} *
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={confirmPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-3.5 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200/50 transition-all pr-12"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className={`absolute ${direction === "rtl" ? "left-4" : "right-4"} top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-900`}
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        name="general_conditions_accepted"
                        checked={formData.general_conditions_accepted}
                        onChange={handleChange}
                        className="w-5 h-5 mt-1 accent-orange-400"
                        required
                      />
                      <label className="text-sm text-blue-700 leading-relaxed">
                        {t("accept_terms")}{" "}
                        {conditions.length > 0 && (
                          <span dangerouslySetInnerHTML={{
                            __html: conditions
                              .map(c => {
                                const title = c.title?.[i18n.language] || c.title?.fr || t("condition");
                                return `<a href="/condition/${c._id}" class="text-orange-500 hover:underline">${title}</a>`;
                              })
                              .join(` ${i18n.language === "ar" ? "و" : "et"} `)
                          }} />
                        )}
                      </label>
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex-1 py-3.5 rounded-xl border-2 border-blue-200 hover:bg-blue-50 text-blue-900 font-medium transition-all"
                      >
                        Retour
                      </button>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-semibold py-3.5 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:opacity-60"
                      >
                        {isLoading ? (
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {t("processing") || "Création..."}
                          </div>
                        ) : (
                          t("create_my_account")
                        )}
                      </button>
                    </div>
                  </form>
                )}

                <p className="text-center text-blue-700 pt-4">
                  <a
                    href="/login"
                    className="font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                  >
                    {t("login")}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}